import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useQueryParams, useSetQueryParams } from 'src/hooks/utils';
import SelectBox from 'src/components/SelectBox';
import { useTranslation } from 'react-i18next';
import S from './Pagination.styles';

const range = (from, to, step = 1) => {
  let i = from;
  const pool = [];

  while (i <= to) {
    pool.push(i);
    i += step;
  }

  return pool;
};

const Pagination = props => {
  const { t } = useTranslation();

  const init = () => {
    let { totalRecords = null, pageNeighbours = 0 } = props;

    const { page = 1, limit = 15 } = useQueryParams(window.location);
    const pageLimit = limit ? parseInt(limit, 10) : 15;
    const currentPage = page ? parseInt(page, 10) : 1;

    totalRecords = typeof totalRecords === 'number' ? totalRecords : 0;
    pageNeighbours =
      typeof pageNeighbours === 'number'
        ? Math.max(0, Math.min(pageNeighbours, 2))
        : 0;

    const totalPages = Math.ceil(totalRecords / pageLimit);

    return {
      pageLimit,
      totalRecords,
      pageNeighbours,
      totalPages,
      currentPage,
    };
  };

  const [state, setState] = useState(() => init());

  useEffect(() => {
    setState(init());
  }, [window.location.search]);

  const firstRun = useRef(true);
  const gotoPage = (page, limit) => {
    const currentPage = Math.max(1, Math.min(page, state.totalPages));
    const pageLimit = limit ? parseInt(limit, 10) : state.pageLimit;
    if (props.useQuery) {
      useSetQueryParams(window.location, {
        page: currentPage,
        limit: pageLimit,
      });
    }
    setState({ ...state, pageLimit, currentPage });
  };

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const { totalRecords, pageNeighbours } = props;
    const totalPages = Math.ceil(totalRecords / state.pageLimit);
    props.refetch({
      offset: (state.currentPage - 1) * state.pageLimit,
      limit: state.pageLimit,
    });
    setState({ ...state, pageNeighbours, totalRecords, totalPages });
  }, [props.totalRecords, state.pageLimit, state.currentPage]);

  const fetchPageNumbers = useCallback(() => {
    const { totalPages } = state;

    return range(1, totalPages);
  }, [state.totalPages, state.currentPage, state.pageNeighbours]);

  if (!state.totalRecords) return null;

  const pages = fetchPageNumbers();
  const listMaxRecord =
    state.pageLimit * state.currentPage > state.totalRecords
      ? state.totalRecords
      : state.pageLimit * state.currentPage;
  const showRecordText = `${state.pageLimit * (state.currentPage - 1) +
    1} — ${listMaxRecord} ${t('OF')} ${state.totalRecords} ${t(
    'RECORDS_SHOWN',
  )}`;
  const lastPage = e => {
    e.preventDefault();
    const page = state.currentPage - 1;
    if (page > 0) {
      gotoPage(page);
    }
  };
  const nextPage = e => {
    e.preventDefault();
    const page = state.currentPage + 1;
    if (page <= pages.length) {
      gotoPage(page);
    }
  };
  return (
    <S.FlexBetween>
      <div>
      <S.LeftText>{showRecordText}</S.LeftText>
      <S.MiddleText>
        {`${t('SHOW_ROWS')} `}
        <S.Select>
          <SelectBox
            onChange={page => {
              gotoPage(1, page.value);
            }}
            options={[15, 30, 50, 100].map(x => {
              return { value: x, label: x };
            })}
            pageStyle
            value={{ value: state.pageLimit, label: state.pageLimit }}
          />
        </S.Select>
      </S.MiddleText>
      <S.MiddleText>
        {`${t('GO_TO')} `}
        <S.Select>
          <SelectBox
            onChange={page => {
              gotoPage(page.value);
            }}
            options={pages.map(page => {
              return { value: page, label: page };
            })}
            pageStyle
            value={{ value: state.currentPage, label: state.currentPage }}
          />
        </S.Select>
        {` ${t('OF')} ${state.totalPages}`}
      </S.MiddleText>
      </div>
      <S.PageNavigation>
        <S.Arrow
          onClick={e => lastPage(e)}
          style={{ pointerEvents: state.currentPage === 1 ? 'none' : 'auto', }}
        >
          <S.IconL name="arrow-l" />
        </S.Arrow>
        <S.Arrow
          onClick={e => nextPage(e)}
          style={{
            pointerEvents: state.currentPage >= pages.length ? 'none' : 'auto',
          }}
        >
          <S.IconR name="arrow-r" />
        </S.Arrow>
      </S.PageNavigation>
    </S.FlexBetween>
  );
};
Pagination.propTypes = {
  currentPage: PropTypes.number,
  pageNeighbours: PropTypes.number,
  setOffset: PropTypes.func,
  totalRecords: PropTypes.number.isRequired,
  pageLimit: PropTypes.number,
  refetch: PropTypes.func,
  useQuery: PropTypes.bool,
};

export default Pagination;
